/**
 * Hooks TanStack Query para el Portal del Colaborador.
 *
 * Funcionan para ambos casos de auth:
 *   - Caso A: portalApiClient (PortalBearer) desde /colaboradores
 *   - Caso B: apiClient estándar (Bearer ERP) desde /portal
 *
 * El hook detecta el contexto por pathname para elegir el cliente correcto.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { SaldoDeBeneficio } from '@/modules/payroll/hooks/useBeneficios'
import { useLocation } from 'react-router-dom'
import { portalApiClient } from '@/store/portalAuthStore'
import apiClient from '@/lib/apiClient'

/** Retorna el cliente API correcto según el contexto de navegación. */
function usePortalClient() {
  const { pathname } = useLocation()
  // Caso A: rutas /colaboradores/* usan portalApiClient (PortalBearer)
  // Caso B: rutas /portal/* usan el apiClient ERP estándar (Bearer)
  return pathname.startsWith('/colaboradores') ? portalApiClient : apiClient
}

/**
 * La raíz de las rutas del portal según el caso de autenticación: `/colaboradores`
 * (cuenta del portal) o `/portal` (usuario del ERP). Los enlaces internos se arman
 * con esto para que una misma pantalla sirva a los dos layouts.
 */
export function usePortalBase(): '/colaboradores' | '/portal' {
  const { pathname } = useLocation()
  return pathname.startsWith('/colaboradores') ? '/colaboradores' : '/portal'
}

// ─── Inicio y ficha (plan del portal, fase 3) ───────────────────────────────

export interface PortalFicha {
  nombre: string
  photo_url: string | null
  cargo: string
  personales: { rut: string; correo: string | null; telefono: string | null; nacimiento: string | null; direccion: string | null }
  trabajo: {
    jefatura: string | null; area: string | null; division: string | null; centro_de_costo: string | null
    ingreso: string | null; antiguedad_anios: number | null; contrato: string | null
    contrato_termino: string | null; jornada_semanal: string | null
  }
  prevision: {
    afp: string | null; salud: string | null; plan_salud_uf: string | null
    cuenta: string | null; cargas_familiares: number | null; sueldo_base: string | null
  }
}

export interface PortalInicio {
  /** Tiene a quién dirigir: equipo directo o el de la jefatura que suple. */
  tiene_equipo: boolean
  /** Solicitudes del equipo que esta persona resuelve como jefatura o suplente. */
  aprobaciones_pendientes: number
  firmas_pendientes: { cantidad: number; primera: { id: string; documento: string; pedida: string } | null }
  ultima_liquidacion: { id: string; anio: number; mes: number; estado: string; liquido: string } | null
  vacaciones: { disponibles: string; legales: number | string; progresivas: number | string; proxima: { desde: string; hasta: string } | null }
  solicitudes: { tipo: 'vacaciones' | 'permiso'; desde: string; hasta: string; estado: string; dias: string; detalle?: string }[]
  documentos_recientes: { id: string; nombre: string; fecha: string }[]
  novedades: unknown[]
}

export function usePortalFicha() {
  const client = usePortalClient()
  return useQuery({
    queryKey: ['portal-ficha'],
    queryFn: async () => (await client.get<PortalFicha>('/employee/ficha/')).data,
  })
}

export function usePortalInicio() {
  const client = usePortalClient()
  return useQuery({
    queryKey: ['portal-inicio'],
    queryFn: async () => (await client.get<PortalInicio>('/employee/home/')).data,
  })
}

// ─── Organigrama y equipo ─────────────────────────────────────────────────────

export interface PortalOrgNode {
  id: string
  name: string
  position: string
  photo_url?: string | null
  subordinate_count: number
  is_me: boolean
  in_my_chain: boolean
  children: PortalOrgNode[]
}

/**
 * El organigrama del portal. ⚠️ Antes la pantalla lo pedía con el cliente del
 * ERP en los dos casos, así que con una cuenta del portal (`/colaboradores`) no
 * cargaba nunca. Un 403 significa que el tenant no lo publica: no se reintenta.
 */
export function usePortalOrgChart() {
  const client = usePortalClient()
  return useQuery({
    queryKey: ['portal-org-chart'],
    queryFn: async () => (await client.get<{ roots: PortalOrgNode[]; total: number }>('/employee/org-chart/')).data,
    retry: (n, e: any) => e?.response?.status !== 403 && n < 2,
  })
}

export interface PortalTeamMember {
  id: string
  name: string
  rut: string
  email: string
  position: string
  department: string
  hire_date: string | null
  vacation_pending_days: number
  has_current_contract: boolean
  photo_url?: string | null
}

/** La ficha de un integrante en modo jefatura: sin datos personales ni previsionales. */
export interface PortalFichaEquipo {
  id: string
  nombre: string
  photo_url: string | null
  cargo: string
  contacto: { correo: string | null; telefono: string | null }
  /** Sin año: es lo único de la fecha de nacimiento que ve la jefatura. */
  cumpleanos: { dia: number; mes: number } | null
  trabajo: PortalFicha['trabajo']
  vacaciones: { disponibles: string; legales: number | string; progresivas: number | string; proxima: { desde: string; hasta: string } | null }
  ausencias: { tipo: 'vacaciones' | 'permiso'; detalle: string; desde: string; hasta: string; dias: string; estado: string }[]
}

export function usePortalTeamMemberFicha(id: string | null) {
  const client = usePortalClient()
  return useQuery({
    queryKey: ['portal-team-member-ficha', id],
    queryFn: async () => (await client.get<PortalFichaEquipo>(`/employee/my-team/${id}/ficha/`)).data,
    enabled: !!id,
  })
}

export function usePortalMyTeam() {
  const client = usePortalClient()
  return useQuery({
    queryKey: ['portal-my-team'],
    queryFn: async () => (await client.get<{ manager: { id: string; name: string }; team: PortalTeamMember[]; count: number }>(
      '/employee/my-team/')).data,
  })
}

// ─── Solicitudes del equipo: la jefatura aprueba desde el portal ──────────────

export interface SolicitudDelEquipo {
  id: string
  kind: 'vacation' | 'leave'
  empleado: { id: string; nombre: string; foto_url: string | null }
  tipo: string
  desde: string
  hasta: string
  dias: string
  motivo: string
  estado: string
  /** Con la 1.ª aprobación y esperando la segunda (#1751): de quién es la primera. */
  primera_aprobacion?: string
  /** Un beneficio de días (#1765): su saldo en el período de la solicitud. */
  saldo_beneficio?: {
    asignado: string; arrastre: string; usados: string; en_tramite: string
    disponible: string; excedido: string; periodo_inicio: string; periodo_fin: string
  } | null
  creada: string
}

export function usePortalTeamRequests() {
  const client = usePortalClient()
  return useQuery({
    queryKey: ['portal-team-requests'],
    queryFn: async () => (await client.get<{ results: SolicitudDelEquipo[]; count: number }>(
      '/employee/my-team/requests/')).data,
  })
}

/** Aprueba o rechaza. Un 409 significa que RRHH (u otra jefatura) ya la resolvió. */
export function useDecideTeamRequest() {
  const client = usePortalClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ solicitud, accion, motivo }: {
      solicitud: SolicitudDelEquipo; accion: 'approve' | 'reject'; motivo?: string
    }) => (await client.post<SolicitudDelEquipo>(
      `/employee/my-team/requests/${solicitud.kind}/${solicitud.id}/${accion}/`,
      motivo ? { motivo } : {},
    )).data,
    // También ante error: un 409 deja la lista vieja, y hay que sacar la resuelta.
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['portal-team-requests'] })
      qc.invalidateQueries({ queryKey: ['portal-inicio'] })
      qc.invalidateQueries({ queryKey: ['portal-my-team'] })
    },
  })
}

// ─── Contraseña del portal (solo cuentas propias, Caso A) ──────────────────────

/** Pide el enlace para crear una contraseña nueva. Responde igual exista o no la cuenta. */
export function usePortalForgotPassword() {
  return useMutation({
    mutationFn: async (email: string) =>
      (await portalApiClient.post<{ detail: string }>('/employee/auth/password/forgot/', { email })).data,
  })
}

export function usePortalChangePassword() {
  return useMutation({
    mutationFn: async (data: { current_password: string; new_password: string }) =>
      (await portalApiClient.post<{ detail: string }>('/employee/auth/password/change/', data)).data,
  })
}

// ─── Employee Me ─────────────────────────────────────────────────────────────

export function usePortalMe() {
  const client = usePortalClient()
  return useQuery({
    queryKey: ['portal-me'],
    queryFn: async () => {
      const res = await client.get('/employee/me/')
      return res.data
    },
  })
}

// ─── Payslips ─────────────────────────────────────────────────────────────────

export function usePortalPayslips() {
  const client = usePortalClient()
  return useQuery({
    queryKey: ['portal-payslips'],
    queryFn: async () => {
      const res = await client.get('/employee/payslips/')
      return res.data
    },
  })
}

export function usePortalPayslipDetail(id: string | undefined) {
  const client = usePortalClient()
  return useQuery({
    queryKey: ['portal-payslip', id],
    queryFn: async () => {
      const res = await client.get(`/employee/payslips/${id}/`)
      return res.data
    },
    enabled: !!id,
  })
}

// ─── Documents ───────────────────────────────────────────────────────────────

export function usePortalDocuments(params?: { search?: string; folder?: string }) {
  const client = usePortalClient()
  return useQuery({
    queryKey: ['portal-documents', params],
    queryFn: async () => {
      const res = await client.get('/employee/documents/', { params })
      return res.data
    },
  })
}

// ─── Portal Login (Caso A) ───────────────────────────────────────────────────

export function usePortalLogin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: { email: string; password: string }) => {
      // Login siempre usa axios plano (sin auth header)
      const res = await portalApiClient.post('/employee/auth/login/', data)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portal-me'] })
      qc.invalidateQueries({ queryKey: ['portal-payslips'] })
    },
  })
}

export function usePortalActivate() {
  return useMutation({
    mutationFn: async (data: { token: string; new_password: string }) => {
      const res = await portalApiClient.post('/employee/auth/activate/', data)
      return res.data
    },
  })
}

export function usePortalLogout() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      await portalApiClient.post('/employee/auth/logout/')
    },
    onSuccess: () => {
      qc.clear()
    },
  })
}

// ─── Admin: gestión de cuentas portal (usa apiClient ERP) ────────────────────

export function usePortalAccountStatus(employeeId: string | undefined) {
  return useQuery({
    queryKey: ['portal-account-status', employeeId],
    queryFn: async () => {
      const res = await apiClient.get(`/payroll/employees/${employeeId}/portal-account/`)
      return res.data
    },
    enabled: !!employeeId,
  })
}

export function useActivatePortalAccount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (employeeId: string) => {
      const res = await apiClient.post(`/payroll/employees/${employeeId}/portal-account/`)
      return res.data
    },
    onSuccess: (_data, employeeId) => {
      qc.invalidateQueries({ queryKey: ['portal-account-status', employeeId] })
    },
  })
}

export function useDeactivatePortalAccount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (employeeId: string) => {
      await apiClient.delete(`/payroll/employees/${employeeId}/portal-account/`)
    },
    onSuccess: (_data, employeeId) => {
      qc.invalidateQueries({ queryKey: ['portal-account-status', employeeId] })
    },
  })
}

export function useResetPortalPassword() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (employeeId: string) => {
      const res = await apiClient.post(`/payroll/employees/${employeeId}/portal-account/reset-password/`)
      return res.data
    },
    onSuccess: (_data, employeeId) => {
      qc.invalidateQueries({ queryKey: ['portal-account-status', employeeId] })
    },
  })
}

// ─── Time off — vacaciones + permisos ───────────────────────────────────────

export interface VacationBalanceByType {
  label: string
  days_entitled: string
  days_taken: string
  /** Saldo DEVENGADO a hoy, no la foto del registro (ver #928). */
  days_pending: string
  days_pending_at_cutoff: string
  days_accrued_since_cutoff: string
  records: number
}

export interface VacationBalance {
  total_days_entitled: string
  total_days_taken: string
  total_days_pending: string
  /** Desglose por bolsa: feriado legal (Art. 67) vs progresivo (Art. 68). */
  by_type?: { legal?: VacationBalanceByType; progressive?: VacationBalanceByType }
}

export interface VacationRequestItem {
  id: string
  start_date: string
  end_date: string
  days_requested: string
  status: 'pending' | 'pending_second' | 'approved' | 'rejected' | 'taken' | 'cancelled'
  status_display: string
  notes: string
  /** Por qué se rechazó o se anuló (#1490). Aparte de `notes`, que es del trabajador. */
  decision_reason?: string
  /** El comprobante que se generó al aprobar y si falta firmarlo (#1489). */
  comprobante?: { document_id: string; firma: 'pending' | 'signed' | null; pedido_id: string | null } | null
  created_at: string
}

export interface LeaveTypeOption { value: string; label: string }

export interface LeaveRequestItem {
  id: string
  leave_type: string
  leave_type_display: string
  start_date: string
  end_date: string
  days: string
  reason: string
  /** `pending_second`: un beneficio de días esperando al segundo aprobador (#1765). */
  status: 'pending' | 'pending_second' | 'approved' | 'rejected' | 'cancelled'
  decision_reason?: string
  status_display: string
  affects_salary: boolean
  beneficio?: string | null
  /** '' | 'manana' | 'tarde' — medio día de un beneficio. */
  parte_del_dia?: string
  document_url: string | null
  created_at: string
}

export interface BusinessDaysDetail {
  business_days: number
  calendar_days: number
  weekend_days: number
  holiday_days: number
  holidays: { date: string; name: string; irrenunciable: boolean }[]
  saturday_inhabil: boolean
  error?: string
}

/**
 * Días hábiles del rango, calculados en el backend con los feriados del país.
 *
 * El portal los contaba en el navegador como días CORRIDOS: alguien que pedía
 * del 6 al 17 de agosto veía 12 días cuando consume 8 (el sábado es inhábil para
 * el feriado, Art. 69 CT), y al aprobarse perdía 4 días de saldo.
 */
export function usePortalBusinessDays(start?: string, end?: string) {
  const client = usePortalClient()
  return useQuery({
    queryKey: ['portal-business-days', start, end],
    enabled: !!start && !!end,
    queryFn: async () => (await client.get<BusinessDaysDetail>(
      '/employee/business-days/', { params: { start, end } })).data,
  })
}

export function usePortalVacationBalance() {
  const client = usePortalClient()
  return useQuery({
    queryKey: ['portal-vacation-balance'],
    queryFn: async () => (await client.get<VacationBalance>('/employee/vacations/balance/')).data,
  })
}

export function usePortalVacationRequests() {
  const client = usePortalClient()
  return useQuery({
    queryKey: ['portal-vacation-requests'],
    queryFn: async () => (await client.get<{ count: number; results: VacationRequestItem[] }>('/employee/vacations/requests/')).data,
  })
}

export function useCreateVacationRequest() {
  const qc = useQueryClient()
  const client = usePortalClient()
  return useMutation({
    mutationFn: async (data: {
      start_date: string; end_date: string; days_requested?: string; notes?: string
      /** Bolsa de la que salen los días: feriado legal (Art. 67) o progresivo (Art. 68). */
      vacation_type?: 'legal' | 'progressive'
    }) => (await client.post('/employee/vacations/requests/', data)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portal-vacation-requests'] })
      qc.invalidateQueries({ queryKey: ['portal-inicio'] })
      qc.invalidateQueries({ queryKey: ['portal-vacation-balance'] })
    },
  })
}

export function useCancelVacationRequest() {
  const qc = useQueryClient()
  const client = usePortalClient()
  return useMutation({
    mutationFn: async (id: string) => (await client.delete(`/employee/vacations/requests/${id}/`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portal-vacation-requests'] })
      qc.invalidateQueries({ queryKey: ['portal-inicio'] })
      qc.invalidateQueries({ queryKey: ['portal-vacation-balance'] })
    },
  })
}

export function usePortalLeaveRequests() {
  const client = usePortalClient()
  return useQuery({
    queryKey: ['portal-leave-requests'],
    queryFn: async () => (await client.get<{
      count: number; results: LeaveRequestItem[]; leave_types: LeaveTypeOption[]
      /** Saldo de cada beneficio de días de la ficha (#1765); vacío si la empresa no usa. */
      beneficios?: SaldoDeBeneficio[]
    }>('/employee/leave-requests/')).data,
  })
}

export function useCreateLeaveRequest() {
  const qc = useQueryClient()
  const client = usePortalClient()
  return useMutation({
    mutationFn: async (data: {
      leave_type: string; start_date: string; end_date: string; days?: string; reason?: string; document?: File
      beneficio?: string; parte_del_dia?: string
    }) => {
      if (data.document) {
        const fd = new FormData()
        Object.entries(data).forEach(([k, v]) => { if (v !== undefined) fd.append(k, v as any) })
        return (await client.post('/employee/leave-requests/', fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data
      }
      return (await client.post('/employee/leave-requests/', data)).data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portal-leave-requests'] })
      qc.invalidateQueries({ queryKey: ['portal-inicio'] })
    },
  })
}

export function useCancelLeaveRequest() {
  const qc = useQueryClient()
  const client = usePortalClient()
  return useMutation({
    mutationFn: async (id: string) => (await client.delete(`/employee/leave-requests/${id}/`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portal-leave-requests'] })
      qc.invalidateQueries({ queryKey: ['portal-inicio'] })
    },
  })
}

// ─── Firma de documentos ──────────────────────────────────────────────────────
// El trabajador firma con un código de un solo uso que le llega al correo. Toda
// la regla dura (tope de intentos, expiración, tope de reenvíos) vive en el
// backend: acá no se replica ninguna, o divergirían.

export interface SignatureRequestItem {
  id: string
  status: 'pending' | 'signed' | 'rejected' | 'cancelled'
  document: {
    id: string
    name: string
    type: string | null
    mime_type: string
    uploaded_at: string
  }
  requested_at: string
  signed_at: string | null
  /** Dónde va a llegar el código. Se muestra para que la persona sepa dónde buscarlo. */
  email_target: string
  code_expires_at: string | null
  attempts_left: number
  resends_left: number
}

export function usePortalSignatures(todos = false) {
  const client = usePortalClient()
  return useQuery({
    queryKey: ['portal-signatures', todos],
    queryFn: async () => (await client.get<{ count: number; results: SignatureRequestItem[] }>(
      '/employee/signatures/', { params: todos ? { status: 'all' } : undefined })).data,
  })
}

export function useRequestSignatureCode() {
  const qc = useQueryClient()
  const client = usePortalClient()
  return useMutation({
    mutationFn: async ({ id, resend }: { id: string; resend?: boolean }) =>
      (await client.post<{ sent_to: string; expires_at: string; resends_left: number }>(
        `/employee/signatures/${id}/code/`, { resend: !!resend })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portal-signatures'] })
      qc.invalidateQueries({ queryKey: ['portal-inicio'] })
    },
  })
}

export function useSignDocument() {
  const qc = useQueryClient()
  const client = usePortalClient()
  return useMutation({
    mutationFn: async ({ id, code, comments }: { id: string; code: string; comments?: string }) =>
      (await client.post<SignatureRequestItem>(
        `/employee/signatures/${id}/sign/`, { code, comments })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portal-signatures'] })
      qc.invalidateQueries({ queryKey: ['portal-inicio'] })
      // El documento firmado pasa a descargarse CON su constancia, así que la
      // lista de documentos también quedó vieja.
      qc.invalidateQueries({ queryKey: ['portal-documents'] })
    },
  })
}

export function useRejectSignature() {
  const qc = useQueryClient()
  const client = usePortalClient()
  return useMutation({
    mutationFn: async ({ id, reason, comments }: { id: string; reason: string; comments?: string }) =>
      (await client.post<SignatureRequestItem>(
        `/employee/signatures/${id}/reject/`, { reason, comments })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portal-signatures'] })
      qc.invalidateQueries({ queryKey: ['portal-inicio'] })
    },
  })
}
